import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaSearch, FaTag } from 'react-icons/fa';
import SearchInput from '../../../components/SearchInput';
import ThematicTagSelect from './ThematicTagSelect';

const SEARCH_PLACEHOLDER = 'Topic title or verse (e.g. 1.5.12)';

type SearchMode = 'keyword' | 'tag';

const keywordPanelAnimation = {
    initial: { translateY: '-100%', opacity: 0 },
    animate: { translateY: '0%', opacity: 1 },
    exit: { translateY: '-100%', opacity: 0 },
    transition: { type: 'spring', duration: 0.7 },
} as const;

const tagPanelAnimation = {
    initial: { translateY: '100%', opacity: 0 },
    animate: { translateY: '0%', opacity: 1 },
    exit: { translateY: '100%', opacity: 0 },
    transition: { type: 'spring', duration: 0.7 },
} as const;

const MODE_BUTTON_BASE_CLASSNAME = 'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors';
const MODE_BUTTON_ACTIVE_CLASSNAME = 'bg-darkBlue text-white dark:bg-lightBlue dark:text-black';
const MODE_BUTTON_INACTIVE_CLASSNAME =
    'text-default-500 hover:bg-default-100 hover:text-darkBlue dark:text-default-400 dark:hover:bg-default-800 dark:hover:text-lightBlue';

export default function ThematicStudiesSearchBar() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const segments = location.pathname.split('/').filter(Boolean);
    const isIndexPage = segments.length <= 1;

    const [mode, setMode] = useState<SearchMode>(() =>
        isIndexPage && searchParams.get('tag') ? 'tag' : 'keyword',
    );

    const query = searchParams.get('q') ?? '';
    const selectedTag = searchParams.get('tag') ?? '';

    // This component stays mounted across every thematic-studies route (it just
    // renders null off the index page), so it must ignore q/tag params that belong
    // to a different page - otherwise a stray tag param picked up while hidden on a
    // theme page would leave the panel stuck on tag mode once back on the index page.
    useEffect(() => {
        if (!isIndexPage) return;

        if (searchParams.get('tag')) {
            setMode('tag');
        } else if (searchParams.get('q')) {
            setMode('keyword');
        }
    }, [isIndexPage, searchParams]);

    const updateSearchParams = useCallback(
        (updates: Record<string, string | null>) => {
            const params = new URLSearchParams(searchParams.toString());
            Object.entries(updates).forEach(([key, value]) => {
                if (value) {
                    params.set(key, value);
                } else {
                    params.delete(key);
                }
            });

            const search = params.toString();
            navigate(search ? `${location.pathname}?${search}` : location.pathname, { replace: true });
        },
        [location.pathname, navigate, searchParams],
    );

    const handleQueryChange = useCallback(
        (value: string) => updateSearchParams({ q: value.trim() || null }),
        [updateSearchParams],
    );

    const handleTagChange = useCallback(
        (tagId: string) => updateSearchParams({ tag: tagId || null }),
        [updateSearchParams],
    );

    const handleModeSelect = useCallback(
        (nextMode: SearchMode) => {
            // The sync effect above guarantees mode already matches which of q/tag is
            // present, so re-selecting the active mode has nothing to clear - skip the
            // redundant history-replace navigation it would otherwise trigger.
            if (nextMode === mode) return;
            setMode(nextMode);
            updateSearchParams(nextMode === 'tag' ? { q: null } : { tag: null });
        },
        [mode, updateSearchParams],
    );

    if (!isIndexPage) return null;

    return (
        <div className="m-8 md:mb-4 md:mt-10 md:mx-28">
            <div
                className="mb-3 inline-flex items-center gap-1 rounded-lg border border-default-200 bg-white/80 p-1 shadow-sm dark:border-default-700 dark:bg-default-900/70"
                role="group"
                aria-label="Search mode"
            >
                <button
                    type="button"
                    onClick={() => handleModeSelect('keyword')}
                    aria-pressed={mode === 'keyword'}
                    title="Search by keyword"
                    className={`${MODE_BUTTON_BASE_CLASSNAME} ${mode === 'keyword' ? MODE_BUTTON_ACTIVE_CLASSNAME : MODE_BUTTON_INACTIVE_CLASSNAME}`}
                >
                    <FaSearch className="text-base" aria-hidden="true" />
                    Search by keyword
                </button>
                <button
                    type="button"
                    onClick={() => handleModeSelect('tag')}
                    aria-pressed={mode === 'tag'}
                    title="Search by tag"
                    className={`${MODE_BUTTON_BASE_CLASSNAME} ${mode === 'tag' ? MODE_BUTTON_ACTIVE_CLASSNAME : MODE_BUTTON_INACTIVE_CLASSNAME}`}
                >
                    <FaTag className="text-base" aria-hidden="true" />
                    Search by tag
                </button>
            </div>
            <div className="mb-5 h-[80px] overflow-hidden">
                {mode === 'keyword' && (
                    <AnimatePresence>
                        <motion.div key="keyword-panel" {...keywordPanelAnimation}>
                            <SearchInput
                                onChangeValue={handleQueryChange}
                                shouldDisable={false}
                                searchDescription={SEARCH_PLACEHOLDER}
                                onResetResult={false}
                                value={query}
                            />
                        </motion.div>
                    </AnimatePresence>
                )}
                {mode === 'tag' && (
                    <AnimatePresence>
                        <motion.div key="tag-panel" {...tagPanelAnimation}>
                            <ThematicTagSelect value={selectedTag} onChangeValue={handleTagChange} />
                        </motion.div>
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
}
